<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/ComponentColoring.vue.
  POST .../update-visualization calls became store.setColoring / setCarbonsOnly / setUniformColor.
  Electrostatic needs a server-computed ESP map, so in Lite it is shown greyed (surface only) with a tooltip.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import Popover from 'primevue/popover';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import ColorPicker from 'primevue/colorpicker';
import { COLORING_MENU, coloringName, useLiteStore, type Component, type VisualizationModel } from '@nanome-lite/state';

const props = defineProps<{ component: Component | null; visualization: VisualizationModel | null }>();
const emit = defineEmits<{ hide: [] }>();

const store = useLiteStore();
const popover = ref<InstanceType<typeof Popover>>();

const currentColoringName = computed(() => props.visualization ? coloringName(props.visualization.coloring) : null);
const isElementTypeColoring = computed(() => currentColoringName.value === 'Element Type');
const isSurfaceVisualization = computed(() => props.visualization?.kind === 'MolecularSurface');

function setColoring(name: string) {
  if (!props.component || !props.visualization) return;
  const preset = COLORING_MENU.find(m => m.name === name)?.preset;
  if (!preset) return;
  store.setColoring(props.component.serial, props.visualization.kind, preset);
}

function setCarbonsOnly(value: boolean) {
  if (!props.component || !props.visualization) return;
  store.setCarbonsOnly(props.component.serial, props.visualization.kind, value);
}

function setUniformColor(color: string) {
  if (!props.component || !props.visualization) return;
  store.setUniformColor(props.component.serial, props.visualization.kind, color);
}
// ComponentColoring.vue:113 — the inline picker fires continuously; debounce 1000 ms as MARA does
const debouncedSetUniformColor = useDebounceFn((c: string) => setUniformColor(c), 1000);

defineExpose({
  show: (e: Event) => popover.value?.show(e),
  hide: () => popover.value?.hide(),
  toggle: (e: Event) => popover.value?.toggle(e),
});
</script>

<template>
  <Popover ref="popover" class="min-w-217px rd-6" pt:content="flex flex-col gap-2" @hide="emit('hide')">
    <div class="flex flex-col gap-1">
      <template v-for="{ name } of COLORING_MENU" :key="name">
        <Button
          v-if="name === 'Electrostatic' ? isSurfaceVisualization : true"
          v-tooltip.left="name === 'Electrostatic' ? 'Electrostatic coloring needs a server-computed map: use Open in Nanome' : undefined"
          :label="name"
          :outlined="currentColoringName !== name"
          :disabled="name === 'Electrostatic' || !visualization"
          severity="secondary"
          size="small"
          @click="setColoring(name)"
        />
      </template>
    </div>
    <label v-if="!isElementTypeColoring" class="flex items-center justify-between gap-2 cursor-pointer">
      Carbons only
      <ToggleSwitch
        :key="`co-${visualization?.coloring.kind}-${visualization?.coloring.carbonsOnly}`"
        :default-value="!!visualization?.coloring.carbonsOnly"
        :disabled="!visualization"
        @value-change="setCarbonsOnly"
      />
    </label>
    <ColorPicker
      v-if="visualization?.coloring.kind === 'Uniform'"
      :default-color="visualization.coloring.uniformColor ?? 'FFFFFF'"
      inline
      @value-change="debouncedSetUniformColor"
    />
  </Popover>
</template>
